import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { api } from "@/lib/api";

interface TicketRef {
  id: string;
  public_id: string;
  title: string;
  state: { display_name: string; category: string };
  product: { display_name: string };
  created_at: string;
  updated_at: string;
}

const STATE_BADGE: Record<string, string> = {
  open: "bg-blue-100 text-blue-800",
  pending: "bg-amber-100 text-amber-900",
  resolved: "bg-emerald-100 text-emerald-900",
  closed: "bg-slate-200 text-slate-700",
};

export default function MyTicketsPage() {
  const { data, isLoading } = useQuery({
    queryKey: ["portal-tickets"],
    queryFn: () => api<{ items: TicketRef[] }>("/portal/tickets"),
  });

  const items = data?.items ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">My tickets</h1>
        <Link
          to="/new"
          className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-500"
        >
          + New ticket
        </Link>
      </div>
      <div className="text-xs text-slate-500">
        <Link to="/docs" className="underline">Browse help docs</Link>
      </div>

      {isLoading ? (
        <div className="text-slate-600">Loading…</div>
      ) : items.length === 0 ? (
        <div className="rounded-md border border-slate-200 bg-white p-6 text-center text-sm text-slate-600">
          No tickets yet. <Link to="/new" className="text-blue-600 underline">File one</Link>.
        </div>
      ) : (
        <ul className="space-y-2">
          {items.map((t) => (
            <li key={t.id}>
              <Link
                to={`/tickets/${t.public_id}`}
                className="flex items-center gap-3 rounded-md border border-slate-200 bg-white px-3 py-2 hover:bg-slate-50"
              >
                <span className="font-mono text-xs text-blue-600">{t.public_id}</span>
                <span className="flex-1 truncate text-sm">{t.title}</span>
                <span className="text-xs text-slate-500">{t.product.display_name}</span>
                <span
                  className={`rounded-full px-2 py-0.5 text-xs ${STATE_BADGE[t.state.category] ?? "bg-slate-100 text-slate-600"}`}
                >
                  {t.state.display_name}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
